// SIB (Sharjah Islamic Bank) — Islamic, debits shown negative / credits positive.
// Transactions extract very cleanly by column; the special Islamic lines
// (Murabaha + Wakala + Bonus) net to zero and are tagged 'islamic_net' to be
// ignored on reconcile, and 'cashback' for the redemption credit.
// NOTE: the summary box carries NO Latin labels (Arabic-only), so its fields
// are read positionally — the one fragile spot if SIB reshuffles the layout.
import { toNum } from '../util.js';

const MONTHS = { JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06', JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12' };
const ddMmmYyyy = (s) => {
  const m = String(s).match(/(\d{1,2})\/([A-Z]{3})\/(\d{4})/i);
  return m ? `${m[3]}-${MONTHS[m[2].toUpperCase()]}-${m[1].padStart(2, '0')}` : null;
};

export function parseSIB(pages) {
  const p1 = pages[0] || [];
  const byY = {};
  for (const it of p1) { const k = Math.round(it.y / 3) * 3; (byY[k] = byY[k] || []).push(it); }
  const rowOf = (y) => (byY[y] || []).slice().sort((a, b) => a.x - b.x);

  // Header row: the one with the DD/MMM/YYYY dates → statement + due + limit.
  const hdrKey = Object.keys(byY).map(Number).find((y) => (byY[y] || []).some((i) => /\d{1,2}\/[A-Z]{3}\/\d{4}/i.test(i.s)));
  const hdr = hdrKey != null ? rowOf(hdrKey) : [];
  const dates = hdr.filter((i) => /\d{1,2}\/[A-Z]{3}\/\d{4}/i.test(i.s)).map((i) => i.s);
  const stmtYear = dates[0] ? +ddMmmYyyy(dates[0]).slice(0, 4) : new Date().getFullYear();
  // limit = the >1000 value sitting at the far right near the header band.
  const limitItem = p1.filter((i) => Math.abs(i.y - (hdrKey ?? 0)) <= 6 && i.x > 460 && /^[\d,]+\.\d{2}/.test(i.s));
  // Min Payment Due sits between the card number and the due date (x≈285),
  // rendered negative like other SIB figures → take the magnitude.
  const minItem = hdr.find((i) => i.x >= 250 && i.x <= 345 && /^-?[\d,]+\.\d{2}$/.test(i.s));

  const summary = {
    statementDate: dates[0] ? ddMmmYyyy(dates[0]) : null,
    dueDate: dates[1] ? ddMmmYyyy(dates[1]) : null,
    creditLimit: limitItem.length ? toNum(limitItem[0].s) : null,
    minimumPayment: minItem ? Math.abs(toNum(minItem.s)) : null,
    availableLimit: null,
    // closing balance = magnitude of the rightmost figure in the signed-summary
    // row directly under the header (positional — no label to anchor on).
    closingBalance: null,
    previousBalance: null,
    cashbackBalance: null, cashbackEarned: null, cashbackRedeemed: null,
  };
  if (hdrKey != null) {
    const below = Object.keys(byY).map(Number).filter((y) => y < hdrKey).sort((a, b) => b - a);
    for (const y of below.slice(0, 3)) {
      const figs = rowOf(y).filter((i) => /-?[\d,]+\.\d{2}/.test(i.s));
      // Row order: Previous | Purchases | Charges | Payments | New (closing).
      // Both balances stored as magnitudes (debits render negative on SIB).
      if (figs.length >= 4) {
        summary.closingBalance = Math.abs(toNum(figs[figs.length - 1].s));
        summary.previousBalance = Math.abs(toNum(figs[0].s));
        break;
      }
    }
  }

  // Cashback summary (AED, no Latin labels to anchor on — read positionally): a
  // row of exactly six whole-number cells →
  //   Opening | Earned | Adjusted | Redeemed | Expired | Balance.
  for (const y of Object.keys(byY).map(Number).sort((a, b) => b - a)) {
    const ints = rowOf(y).filter((i) => /^\d{1,7}$/.test(i.s));
    if (ints.length === 6) {
      const v = ints.map((i) => toNum(i.s));
      summary.cashbackEarned = v[1];
      summary.cashbackRedeemed = v[3];
      summary.cashbackBalance = v[5];
      break;
    }
  }

  // Transactions: date@~54 (DD/MM) · posting@~111 · merchant@~145 [· loc · country] · ccy@~368 · orig@~434 · AED@~520
  const txns = [];
  for (const page of pages) {
    const rb = {};
    for (const it of page) { const k = Math.round(it.y / 3) * 3; (rb[k] = rb[k] || []).push(it); }
    for (const y of Object.keys(rb).map(Number).sort((a, b) => b - a)) {
      const cells = rb[y].sort((a, b) => a.x - b.x);
      const first = cells[0];
      if (!first || first.x > 70 || !/^\d{1,2}\/\d{1,2}$/.test(first.s)) continue;
      const aedCell = [...cells].reverse().find((c) => c.x > 500 && /^-?[\d,]+\.\d{2}$/.test(c.s));
      if (!aedCell) continue;
      const ccyCell = cells.find((c) => c.x > 350 && c.x < 400 && /^[A-Z]{3}$/.test(c.s));
      const origCell = ccyCell ? cells.find((c) => c.x > 400 && c.x < 480 && /^-?[\d,]+\.\d{2}$/.test(c.s)) : null;
      const mid = cells.filter((c) => c.x >= 140 && c.x < 360);
      const desc = mid.map((c) => c.s).join(' ').replace(/\s{2,}/g, ' ').trim();
      if (!desc) continue;
      // SIB convention: negative = debit (spend/charge), positive = credit
      // (payment/refund/cashback). Opposite of the CR-suffix banks.
      const raw = parseFloat(aedCell.s.replace(/[^0-9.\-]/g, ''));
      const amount = Math.abs(raw);
      const credit = raw > 0;
      const tag = /CASHBACK REDEEMED/i.test(desc) ? 'cashback'
        : /MURABAHA PROFIT|WAKALA PROFIT|^BONUS/i.test(desc) ? 'islamic_net'
        : /Credit Card Payment|Payment Received/i.test(desc) ? 'payment' : null;
      const [d, mo] = first.s.split('/');
      // The rightmost column is the AED equivalent (what `amount` holds); the
      // ccy/orig columns are the foreign side. Store AED as `currency`, foreign
      // as originalCurrency/originalAmount (consistent with the other banks).
      const fxCcy = (ccyCell && ccyCell.s !== 'AED') ? ccyCell.s : null;
      txns.push({
        date: `${stmtYear}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`,
        description: desc,
        amount, credit,
        currency: 'AED',
        originalCurrency: fxCcy,
        originalAmount: (fxCcy && origCell) ? Math.abs(toNum(origCell.s)) : null,
        tag,
      });
    }
  }

  return { bank: 'SIB', summary, transactions: txns };
}
